import type { AIToolsConfigService } from '@affine/core/modules/ai-button';
import type { AIModelService } from '@affine/core/modules/ai-button/services/models';
import type {
  ServerService,
  SubscriptionService,
} from '@affine/core/modules/cloud';
import {
  type CopilotChatHistoryFragment,
  ServerDeploymentType,
  SubscriptionStatus,
} from '@affine/graphql';
import {
  menu,
  popMenu,
  popupTargetFromElement,
} from '@blocksuite/affine/components/context-menu';
import { SignalWatcher, WithDisposable } from '@blocksuite/affine/global/lit';
import { unsafeCSSVarV2 } from '@blocksuite/affine/shared/theme';
import type { NotificationService } from '@blocksuite/affine-shared/services';
import {
  AiOutlineIcon,
  ArrowDownSmallIcon,
  CloudWorkspaceIcon,
  DoneIcon,
  LockIcon,
  QuoteIcon,
  ThinkingIcon,
  WebIcon,
} from '@blocksuite/icons/lit';
import { ShadowlessElement } from '@blocksuite/std';
import { computed } from '@preact/signals-core';
import { css, html } from 'lit';
import { property } from 'lit/decorators.js';

export class ChatInputPreference extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  static override styles = css`
    .chat-input-preference-trigger {
      display: flex;
      align-items: center;
      padding: 0px 4px;
      color: var(--affine-v2-icon-primary);
      transition: all 0.23s ease;
      border-radius: 4px;
      background: transparent;
      border: none;
      cursor: pointer;
    }
    .chat-input-preference-trigger:hover {
      background-color: var(--affine-v2-layer-background-hoverOverlay);
    }
    .chat-input-preference-trigger-label {
      font-size: 14px;
      line-height: 22px;
      font-weight: 500;
      padding: 0px 4px;
    }
    .chat-input-preference-trigger-icon {
      font-size: 20px;
      line-height: 0;
    }
    .preference-action {
      white-space: nowrap;
      min-width: 220px;
    }
    .ai-active-model-name {
      font-size: 14px;
      color: ${unsafeCSSVarV2('text/secondary')};
      line-height: 22px;
      margin-left: 40px;
    }
    .ai-model-prefix {
      width: 20px;
      height: 20px;
    }
    .ai-model-prefix svg {
      color: ${unsafeCSSVarV2('icon/activated')};
    }
    .ai-model-postfix svg:hover {
      color: ${unsafeCSSVarV2('icon/activated')};
    }
    .ai-model-version {
      font-size: 12px;
      color: ${unsafeCSSVarV2('text/tertiary')};
      line-height: 20px;
      margin-right: 40px;
    }
  `;

  @property({ attribute: false })
  accessor session!: CopilotChatHistoryFragment | null | undefined;
  // --------- model props end ---------

  // --------- extended thinking props start ---------
  @property({ attribute: false })
  accessor extendedThinking: boolean = false;

  @property({ attribute: false })
  accessor onExtendedThinkingChange:
    | ((extendedThinking: boolean) => void)
    | undefined;
  // --------- extended thinking props end ---------

  // --------- citations props start ---------
  @property({ attribute: false })
  accessor citations: boolean = false;

  @property({ attribute: false })
  accessor onCitationsChange:
    | ((citations: boolean) => void)
    | undefined;
  // --------- citations props end ---------

  // --------- search props start ---------
  @property({ attribute: false })
  accessor networkSearchVisible: boolean = false;

  @property({ attribute: false })
  accessor isNetworkActive: boolean = false;

  @property({ attribute: false })
  accessor onNetworkActiveChange:
    | ((isNetworkActive: boolean) => void)
    | undefined;
  // --------- search props end ---------

  @property({ attribute: false })
  accessor serverService!: ServerService;

  @property({ attribute: false })
  accessor toolsConfigService!: AIToolsConfigService;

  @property({ attribute: false })
  accessor notificationService!: NotificationService;

  @property({ attribute: false })
  accessor subscriptionService!: SubscriptionService;

  @property({ attribute: false })
  accessor aiModelService!: AIModelService;

  @property({ attribute: false })
  accessor onAISubscribe!: () => Promise<void>;

  model = computed(() => {
    const modelId = this.aiModelService.modelId.value;
    const activeModel = this.aiModelService.models.value.find(
      model => model.id === modelId
    );
    const defaultModel = this.aiModelService.models.value.find(
      model => model.isDefault
    );
    return activeModel || defaultModel;
  });

  openPreference(e: Event) {
    const element = e.currentTarget;
    if (!(element instanceof HTMLElement)) return;
    const modelItems = [];
    const searchItems = [];

    // model switch
    modelItems.push(
      menu.subMenu({
        name: 'Model',
        prefix: AiOutlineIcon(),
        postfix: html`
          <span class="ai-active-model-name"> ${this.model.value?.name} </span>
        `,
        options: {
          items: this.aiModelService.models.value.map(model => {
            const isSelected = model.id === this.model.value?.id;
            const isSelfHosted =
              this.serverService.server.config$.value?.type ===
              ServerDeploymentType.Selfhosted;
            const status =
              this.subscriptionService.subscription.ai$.value?.status;
            const isSubscribed = status === SubscriptionStatus.Active;
            return menu.action({
              name: model.category,
              info: html`
                <span class="ai-model-version">${model.version}</span>
              `,
              prefix: html`
                <div class="ai-model-prefix">
                  ${isSelected ? DoneIcon() : undefined}
                </div>
              `,
              postfix: html`
                <div class="ai-model-postfix" @click=${this.onAISubscribe}>
                  ${model.isPro && !isSubscribed ? LockIcon() : undefined}
                </div>
              `,
              select: () => {
                if (model.isPro && !isSelfHosted && !isSubscribed) {
                  this.notificationService.toast(
                    `Pro models require an AFFiNE AI subscription.`
                  );
                  return;
                }
                this.aiModelService.setModel(model.id);
              },
            });
          }),
        },
      })
    );

    modelItems.push(
      menu.toggleSwitch({
        name: 'Extended Thinking',
        prefix: ThinkingIcon(),
        on: this.extendedThinking,
        onChange: (value: boolean) => this.onExtendedThinkingChange?.(value),
        class: { 'preference-action': true },
      })
    );

    modelItems.push(
      menu.toggleSwitch({
        name: 'Citations',
        prefix: QuoteIcon(),
        on: this.citations,
        onChange: (value: boolean) => this.onCitationsChange?.(value),
        class: { 'preference-action': true },
      })
    );

    if (this.networkSearchVisible) {
      searchItems.push(
        menu.toggleSwitch({
          name: 'Web Search',
          prefix: WebIcon(),
          on: this.isNetworkActive,
          onChange: (value: boolean) => this.onNetworkActiveChange?.(value),
          class: { 'preference-action': true },
          testId: 'chat-network-search',
        }),
        menu.toggleSwitch({
          name: 'Workspace All Docs',
          prefix: CloudWorkspaceIcon(),
          on:
            !!this.toolsConfigService.config.value.searchWorkspace &&
            !!this.toolsConfigService.config.value.readingDocs,
          onChange: (value: boolean) =>
            this.toolsConfigService.setConfig({
              searchWorkspace: value,
              readingDocs: value,
            }),
          class: { 'preference-action': true },
        })
      );
    }

    popMenu(popupTargetFromElement(element), {
      options: {
        items: [
          menu.group({
            items: [...modelItems],
          }),
          menu.group({
            items: [...searchItems],
          }),
        ],
        testId: 'chat-input-preference',
      },
    });
  }

  override render() {
    return html`<button
      @click=${this.openPreference}
      data-testid="chat-input-preference-trigger"
      class="chat-input-preference-trigger"
    >
      <span class="chat-input-preference-trigger-label">
        ${this.model.value?.category}
      </span>
      <span class="chat-input-preference-trigger-icon">
        ${ArrowDownSmallIcon()}
      </span>
    </button>`;
  }
}
